import React, { useState, useEffect, useCallback } from 'react'
import { TaskCard, TaskColumn, TaskBoardType, TaskCardDependency } from '../../types/task-board'
import { useRequests } from '../../requests/useRequests'
import { RefreshActions } from '../../state/refresh'
import { useDispatch } from 'react-redux'
import { DEPENDENCY_TYPES } from '../../common/task-constants'

// Importuj nowe komponenty
import { TaskCardHeader } from './TaskCardHeader'
import { TaskCardDescription } from './TaskCardDescription'
import { TaskCardComments } from './TaskCardComments'
import { TaskCardAssignee } from './TaskCardAssignee'
import { TaskCardMoveColumn } from './TaskCardMoveColumn'
import { TaskCardDependencies } from './TaskCardDependencies'
import { TaskCardPosition } from './TaskCardPosition'
import { TaskCardPriority } from './TaskCardPriority'
import { AddDependencyModal } from './AddDependencyModal'

interface TaskColumnCardProps {
  card: TaskCard
  column: TaskColumn
  board: TaskBoardType
  showCardModal: { columnId: string, cardId: string } | null
  setShowCardModal: (showCardModal: { columnId: string, cardId: string } | null) => void
  handleAddComment: (cardId: string, commentContent: string) => Promise<void>
  handleDeleteCard: (columnId: string, cardId: string) => void
  handleAssignUser: (cardId: string, userId: string | null) => Promise<void>
  handleUpdateCardDescription: (cardId: string, description: string) => Promise<void>
  handleMoveCard: (cardId: string, sourceColumnId: string, destinationColumnId: string) => Promise<void>
  handleChangeCardPosition: (cardId: string, columnId: string, direction: 'up' | 'down') => Promise<void>
  handleUpdateCardPriority: (cardId: string, priority: number) => Promise<void>
  handleAddDependency: (cardId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => Promise<void>
  handleUpdateDependency: (cardId: string, dependencyId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => Promise<void>
  handleRemoveDependency: (cardId: string, dependencyId: string) => Promise<void>
  handleUpdateNotifyOnComplete: (cardId: string, notifyOnComplete: boolean) => Promise<void>
  handleUpdateCardDueDates: (cardId: string, dueDate: string | null, dueDateTo: string | null) => Promise<void>
}

export const TaskColumnCard = ({
  card,
  column,
  board,
  showCardModal,
  setShowCardModal,
  handleAddComment,
  handleDeleteCard,
  handleAssignUser,
  handleUpdateCardDescription,
  handleMoveCard,
  handleChangeCardPosition,
  handleUpdateCardPriority,
  handleAddDependency,
  handleUpdateDependency,
  handleRemoveDependency,
  handleUpdateNotifyOnComplete,
  handleUpdateCardDueDates
}: TaskColumnCardProps) => {
  const requestMaker = useRequests()
  const dispatch = useDispatch()

  // Stany dla formularzy i danych
  const [showAddDependencyModal, setShowAddDependencyModal] = useState(false)
  const [dependencies, setDependencies] = useState<TaskCardDependency[]>([])
  const [editDueDate, setEditDueDate] = useState(false)
  const [dueDateInput, setDueDateInput] = useState(card.dueDate ? card.dueDate.slice(0, 16) : '')
  const [dueDateToInput, setDueDateToInput] = useState(card.dueDateTo ? card.dueDateTo.slice(0, 16) : '')

  // Pobierz zależności dla karty
  const fetchDependencies = useCallback(async () => {
    if (!board?.id || !card?.id) return
    try {
      const response = await requestMaker.getTaskCardDependencies(board.id, card.id)
      setDependencies(response.data)
    } catch (error) {
      console.error('Błąd podczas pobierania zależności:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać zależności'
        })
      )
    }
  }, [requestMaker, board?.id, card?.id, dispatch])

  useEffect(() => {
    fetchDependencies()
  }, [fetchDependencies])

  const handleAddDependencyWrapper = async (dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => {
    if (!board?.id || !card?.id) return
    try {
      await handleAddDependency(card.id, dependencyData)
      fetchDependencies()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało dodane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas dodawania powiązania:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się dodać powiązania'
        })
      )
      throw error
    }
  }

  const handleUpdateDependencyWrapper = async (dependencyId: string, dependencyData: {
    dependencyType: string
    stageName: string
    customTaskName: string
  }) => {
    if (!board?.id || !card?.id) return
    try {
      await handleUpdateDependency(card.id, dependencyId, dependencyData)
      fetchDependencies()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało zaktualizowane'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji powiązania:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować powiązania'
        })
      )
    }
  }

  const handleRemoveDependencyWrapper = async (dependencyId: string) => {
    if (!board?.id || !card?.id) return
    try {
      await handleRemoveDependency(card.id, dependencyId)
      fetchDependencies()
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Powiązanie zostało usunięte'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania powiązania:', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć powiązania'
        })
      )
    }
  }

  const handleUpdatePriority = async (priority: number) => {
    if (!card?.id) return
    try {
      await handleUpdateCardPriority(card.id, priority)
    } catch (error) {
      console.error('Błąd podczas aktualizacji priorytetu:', error)
    }
  }

  const getDependencyTypeLabel = (type: string) => {
    const dependency = DEPENDENCY_TYPES.find(d => d.value === type)
    return dependency?.label || type
  }

  const handleAssignUserWrapper = async (cardId: string, userId: string | null) => {
    if (showCardModal != null) {
      return await handleAssignUser(cardId, userId)
    }
    return await Promise.resolve()
  }

  const handleMoveCardWrapper = async (cardId: string, sourceColumnId: string, destinationColumnId: string) => {
    if (showCardModal != null) {
      return await handleMoveCard(cardId, showCardModal.columnId, destinationColumnId)
    }
    return await Promise.resolve()
  }

  const handleChangePositionWrapper = async (cardId: string, columnId: string, direction: 'up' | 'down') => {
    if (showCardModal != null) {
      return await handleChangeCardPosition(cardId, showCardModal.columnId, direction)
    }
    return await Promise.resolve()
  }

  const handleDeleteClick = () => {
    if (window.confirm('Czy na pewno chcesz usunąć to zadanie?') && (showCardModal != null)) {
      handleDeleteCard(showCardModal.columnId, showCardModal.cardId)
    }
  }

  if (!card || !column || (showCardModal == null)) {
    return null
  }

  return (
    <div key={card.id}>
      <TaskCardHeader card={card} onClose={() => setShowCardModal(null)} />

      <div style={{ padding: window.innerWidth < 768 ? '15px' : '20px' }}>
        <div style={{
          display: 'flex',
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          flexWrap: 'wrap',
          margin: window.innerWidth < 768 ? 0 : '0 -10px'
        }}
        >
          <div style={{
            flex: window.innerWidth < 768 ? 'auto' : '1 1 60%',
            minWidth: window.innerWidth < 768 ? 'auto' : '300px',
            padding: window.innerWidth < 768 ? 0 : '0 10px',
            boxSizing: 'border-box',
            marginBottom: window.innerWidth < 768 ? '20px' : 0
          }}
          >
            <TaskCardDescription card={card} onUpdateDescription={handleUpdateCardDescription} />

            {/* Termin zadania */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: window.innerWidth < 768 ? '15px' : '16px', fontWeight: 600, margin: 0 }}>Terminy</h4>
              {editDueDate
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input
                      type='datetime-local'
                      value={dueDateInput}
                      onChange={e => setDueDateInput(e.target.value)}
                      style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px' }}
                    />
                    <span style={{ fontSize: '14px' }}> - </span>
                    <input
                      type='datetime-local'
                      value={dueDateToInput}
                      onChange={e => setDueDateToInput(e.target.value)}
                      style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px' }}
                    />
                    <button
                      onClick={async () => {
                        await handleUpdateCardDueDates(card.id, dueDateInput ? new Date(dueDateInput).toISOString() : null, dueDateToInput ? new Date(dueDateToInput).toISOString() : null)
                        setEditDueDate(false)
                      }}
                      style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', padding: '5px 10px', fontSize: '13px', marginRight: 4 }}
                    >Zapisz
                    </button>
                    <button
                      onClick={() => {
                        setEditDueDate(false)
                        setDueDateInput(card.dueDate ? card.dueDate.slice(0, 16) : '')
                      }}
                      style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', padding: '5px 10px', fontSize: '13px' }}
                    >Anuluj
                    </button>
                  </div>
                  )
                : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <span style={{ fontSize: '14px' }}>
                      {card.dueDate && card.dueDateTo ? new Date(card.dueDate).toLocaleString() + ' - ' + new Date(card.dueDateTo).toLocaleString() : 'Brak terminu'}
                    </span>
                    <button
                      onClick={() => setEditDueDate(true)}
                      style={{ backgroundColor: 'transparent', border: 'none', color: '#007bff', fontSize: '13px', cursor: 'pointer', padding: '3px 8px', borderRadius: '3px' }}
                    >Edytuj
                    </button>
                    {card.dueDate && (
                      <button
                        onClick={async () => {
                          await handleUpdateCardDueDates(card.id, null, null)
                          setEditDueDate(false)
                          setDueDateInput('')
                          setDueDateToInput('')
                        }}
                        style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', padding: '5px 10px', fontSize: '13px' }}
                      >Usuń terminy
                      </button>
                    )}
                  </div>
                  )}
            </div>

            <TaskCardComments card={card} onAddComment={handleAddComment} />

            <div className='task-card-options'>
              <label className='notify-on-complete'>
                <input
                  type='checkbox'
                  checked={card.notifyOnComplete}
                  onChange={async (e) => await handleUpdateNotifyOnComplete(card.id, e.target.checked)}
                />
                Powiadom email po zakończeniu
              </label>
            </div>
          </div>

          <div style={{
            flex: window.innerWidth < 768 ? 'auto' : '1 1 40%',
            minWidth: window.innerWidth < 768 ? 'auto' : '250px',
            padding: window.innerWidth < 768 ? 0 : '0 10px',
            boxSizing: 'border-box'
          }}
          >
            <TaskCardPriority
              card={card}
              onUpdatePriority={handleUpdatePriority}
            />

            <TaskCardAssignee
              card={card}
              board={board}
              onAssignUser={async (userId) => await handleAssignUserWrapper(card.id, userId)}
            />

            <TaskCardMoveColumn
              card={card}
              currentColumnId={showCardModal.columnId}
              board={board}
              onMoveCard={async (cardId, _, destColId) => await handleMoveCardWrapper(cardId, showCardModal.columnId, destColId)}
            />

            <TaskCardDependencies
              dependencies={dependencies}
              onShowAddDependencyModal={() => setShowAddDependencyModal(true)}
              onRemoveDependency={handleRemoveDependencyWrapper}
              getDependencyTypeLabel={getDependencyTypeLabel}
            />

            <TaskCardPosition
              card={card}
              column={column}
              onChangePosition={async (cardId, _, direction) => await handleChangePositionWrapper(cardId, showCardModal.columnId, direction)}
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', borderTop: '1px solid #e9ecef' }}>
        <button
          type='button'
          className={`
              tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
              tw-text-white
              tw-px-4 tw-py-2 tw-text-sm tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
            `}
          onClick={handleDeleteClick}
        >
          <i className='fa fa-trash mr-1' /> Usuń zadanie
        </button>
      </div>

      {showAddDependencyModal && (
        <AddDependencyModal
          onClose={() => setShowAddDependencyModal(false)}
          onAddDependency={handleAddDependencyWrapper}
          onUpdateDependency={handleUpdateDependencyWrapper}
          onRemoveDependency={handleRemoveDependencyWrapper}
          existingDependencies={dependencies as any[]}
        />
      )}
    </div>
  )
}
